import { useMemo } from 'react'
import { InspectorDataArtboard, InspectorDataLayer } from './inspectorDataType'
import {
  InspectorLayersTreeNode,
  InspectorLayersTreeSymbolLayerNode,
  BasicArtboardLayerPropsFromInspectorJsonFile,
  SymbolMasterArtboardLayerPropsFromInspectorJsonFile,
  BasicLayerPropsFromInspectorJsonFile,
  SymbolLayerPropsFromInspectorJsonFile,
  LayerAppearance,
  LayerCorners,
  SketchElementNode,
  SymbolMaster,
  isArtboardElement,
} from './sketchSceneLib'

import _sortBy from 'lodash/sortBy'
import { useSketchSceneTree } from './useSketchSceneTree'
import { isGroup } from './sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

/**
 * Data coming from the artboard GraphQL query that we need want to use
 * when building our inspector tree.
 */
type ArtboardDataFromGraphQL = {
  /**
   * @see BuildInspectorLayersTreeNodeContext.artboardIsSymbolMaster
   */
  artboardIsSymbolMaster: boolean
}

/**
 * Use the layers tree created from the PRMarinaFile, to build a similar tree
 * but also containing for each node layer information coming from the inspector data.
 */
export function useAugmentLayersTreeWithInspectorData(
  inspectorData: InspectorDataArtboard | null,
  /**
   * Data coming from the artboard GraphQL query.
   */
  artboardGraphQLData: ArtboardDataFromGraphQL | undefined
): InspectorLayersTreeNode | null {
  const sketchSceneRootNode = useSketchSceneTree()

  return useMemo(() => {
    if (!sketchSceneRootNode || !inspectorData || !artboardGraphQLData) {
      return null
    }

    const { artboardIsSymbolMaster } = artboardGraphQLData

    // Map the layers by "order" field instead of per layer uuid.
    const layersByOrderIndex = getLayersInInspectorDataByComputedOrderIndex(
      inspectorData.layers
    )

    verifyTreeLayerMatchingThroughPosition(
      sketchSceneRootNode,
      layersByOrderIndex
    )

    return buildInspectorLayersTreeNode(
      sketchSceneRootNode,
      layersByOrderIndex,
      inspectorData,
      {
        artboardIsSymbolMaster,
        isInsideGroupWithExports: false,
        isInsideGroupWithStyles: false,
        isInsideGroupWithTints: false,
      }
    )
  }, [sketchSceneRootNode, inspectorData, artboardGraphQLData])
}

type BuildInspectorLayersTreeNodeContext = {
  /**
   * True when the artboard at the root of the tree is the artboard of a symbol.
   * We need to pass this in the context because the information comes from
   * graphql.
   */
  artboardIsSymbolMaster: boolean
  /**
   * When parentSymbol exist inside the context, it means that
   * the current node is a child of a symbol instance.
   */
  parentSymbolLayer?: InspectorLayersTreeSymbolLayerNode
  isInsideGroupWithExports: boolean
  isInsideGroupWithStyles: boolean
  isInsideGroupWithTints: boolean
}

function buildInspectorLayersTreeNode(
  sketchElementNode: SketchElementNode,
  layersByOrderIndex: Record<number, InspectorDataLayer>,
  inspectorData: InspectorDataArtboard,
  context: BuildInspectorLayersTreeNodeContext
): InspectorLayersTreeNode {
  const nodeIndex = sketchElementNode.elementRenderingIndex

  // We want to match layers from the layers tree (derived from PRMarinaFile) and
  // the layers coming from the inspector data json file, based on their respective "elementRenderingIndex"
  // and "order" properties. This a workaround because layers none of the uuid nor exportId are matching in 100% of the scenario.
  // "order" or "elementRenderingIndex" should be unique and should match. (See https://github.com/sketch-hq/Cloud/issues/17485 for context)
  // Note: "elementRenderingIndex" is generated on the FE in buildLayersTreeFromPRFile function.
  const layerInInspectorData = layersByOrderIndex[nodeIndex]

  if (!isArtboardElement(sketchElementNode) && !layerInInspectorData) {
    throw new Error(
      `Layer "${sketchElementNode.name}" with uuid ${sketchElementNode.nodeUUID} (${nodeIndex}) not found in json data.`
    )
  }

  const missingProps = isArtboardElement(sketchElementNode)
    ? getArtboardMissingProps(sketchElementNode, inspectorData, context)
    : getLayerMissingProps(layerInInspectorData, inspectorData, context)

  // Omit props like children and type, and name (deprecated because not always correct)
  // keep everything else.
  const { children, ...propsToKeep } = sketchElementNode

  // Create the node without the children, so that we can then use
  // the reference and assign it to parentSymbolLayer when calling buildInspectorLayersTreeNode
  const newAugmentedNode: InspectorLayersTreeNode = {
    ...propsToKeep,
    ...missingProps,
    // Empty children for now, we will fill them later.
    children: [],
  } as InspectorLayersTreeNode // Cheating but this file is meant to be removed soon

  const contextForChildren = getContextForChildren(context, newAugmentedNode)

  // Fill the children as a second step so we can access newAugmentedNode object reference
  // and set it in the context
  newAugmentedNode.children = sketchElementNode.children.map(child => {
    return buildInspectorLayersTreeNode(
      child,
      layersByOrderIndex,
      inspectorData,
      contextForChildren
    )
  })

  return newAugmentedNode
}

/**
 * Read the inspector data from the json file to complete the inspector data we couldn't
 * extract from the PR file for the current artboard.
 */
function getArtboardMissingProps(
  sketchElementNode: SketchElementNode,
  inspectorData: InspectorDataArtboard,
  context: BuildInspectorLayersTreeNodeContext
):
  | BasicArtboardLayerPropsFromInspectorJsonFile
  | SymbolMasterArtboardLayerPropsFromInspectorJsonFile {
  const missingProps: BasicArtboardLayerPropsFromInspectorJsonFile = {
    isExportable: Boolean(inspectorData.exportFormats?.length),
    exportFormats: inspectorData.exportFormats ?? [],
    exportId: sketchElementNode.nodeUUID,
  }

  if (context.artboardIsSymbolMaster) {
    const { symbolName, symbolNamePath } = splitSymbolMasterArtboardNameAndPath(
      sketchElementNode.name
    )
    return {
      ...missingProps,
      symbolMasterShortName: symbolName,
      symbolMasterNamePath: symbolNamePath,
    }
  }
  return {
    ...missingProps,
  }
}

export const splitSymbolMasterArtboardNameAndPath = (artboardName: string) => {
  const symbolNamePathAsList = artboardName.split('/') || []
  // Remove last element which is the actual name of the symbol
  const symbolName = symbolNamePathAsList.pop()
  if (symbolName && symbolNamePathAsList.length > 0) {
    return {
      // Clean the name
      symbolName: symbolName.trim(),
      // Clean the path
      symbolNamePath: symbolNamePathAsList.filter(Boolean).map(p => p.trim()),
    }
  }

  return {}
}

/**
 * Read the inspector data from the json file to complete the inspector data we couldn't
 * extract from the PR file for the current layer.
 */
function getLayerMissingProps(
  layerInInspectorData: InspectorDataLayer,
  inspectorData: InspectorDataArtboard,
  context: BuildInspectorLayersTreeNodeContext
):
  | BasicLayerPropsFromInspectorJsonFile
  | SymbolLayerPropsFromInspectorJsonFile {
  const missingProps: BasicLayerPropsFromInspectorJsonFile = {
    isLayerMasked:
      'maskedBy' in layerInInspectorData
        ? Boolean(layerInInspectorData.maskedBy)
        : false,
    isExportable: layerInInspectorData.exportFormats.length > 0,
    parentSymbolLayer: context.parentSymbolLayer,
    rotation:
      'rotation' in layerInInspectorData
        ? layerInInspectorData.rotation ?? 0
        : 0,
    exportFormats: layerInInspectorData.exportFormats ?? [],
    corners: getLayerCorners(layerInInspectorData),
    appearance: getLayerAppearance(layerInInspectorData),
    exportId: layerInInspectorData.exportID,
    style:
      'style' in layerInInspectorData ? layerInInspectorData.style : undefined,
    text:
      'attributedString' in layerInInspectorData
        ? layerInInspectorData.attributedString
        : undefined,
    textStyleComponent: layerInInspectorData.sharedStyleID
      ? inspectorData.textStyles[layerInInspectorData.sharedStyleID]
      : undefined,
    layerStyleComponent: layerInInspectorData.sharedStyleID
      ? inspectorData.layerStyles[layerInInspectorData.sharedStyleID]
      : undefined,
    isLocked: layerInInspectorData.isLocked,
    isInsideGroupWithExports: context.isInsideGroupWithExports,
    isInsideGroupWithStyles: context.isInsideGroupWithStyles,
    isInsideGroupWithTints: context.isInsideGroupWithTints,
  }

  const symbolMaster = getSymbolMasterIfIsSymbolInstance(
    layerInInspectorData,
    inspectorData.referencedSymbols
  )

  if (symbolMaster) {
    return {
      symbolMaster,
      ...missingProps,
    }
  }

  return {
    ...missingProps,
  }
}

function getSymbolMasterIfIsSymbolInstance(
  layerInInspectorData: InspectorDataLayer,
  referencedSymbols: InspectorDataArtboard['referencedSymbols']
): SymbolMaster | undefined {
  if (
    'symbolMasterID' in layerInInspectorData &&
    layerInInspectorData.symbolMasterID &&
    layerInInspectorData.isSymbolRoot
  ) {
    const symbolMaster = referencedSymbols[layerInInspectorData.symbolMasterID]
    if (!symbolMaster) {
      return undefined
    }
    const { symbolName, symbolNamePath } = splitSymbolMasterArtboardNameAndPath(
      symbolMaster.name
    )

    const baseSymbolMasterProps = {
      name: symbolMaster.name,
      symbolMasterId: layerInInspectorData.symbolMasterID,
      artboardUuid: symbolMaster.id,
      symbolMasterShortName: symbolName,
      symbolMasterNamePath: symbolNamePath,
    }

    if (
      symbolMaster.isForeign &&
      symbolMaster.documentID &&
      symbolMaster.documentName
    ) {
      return {
        ...baseSymbolMasterProps,
        isForeign: true,
        documentUuid: symbolMaster.documentID,
        documentName: symbolMaster.documentName,
      }
    }

    return {
      ...baseSymbolMasterProps,
      isForeign: false,
    }
  }

  // Not a symbol instance
  return undefined
}

function getLayerCorners(
  layerInInspectorData: InspectorDataLayer
): LayerCorners | undefined {
  let cornerType =
    'cornerStyle' in layerInInspectorData
      ? layerInInspectorData.cornerStyle
      : undefined

  const cornerRadius =
    'cornerRadius' in layerInInspectorData
      ? layerInInspectorData.cornerRadius
      : undefined

  const smoothCorners =
    'smoothCorners' in layerInInspectorData &&
    layerInInspectorData.smoothCorners

  if (cornerRadius?.length) {
    if (!cornerType) {
      // If there is a radius but type is not defined, it means is an old document
      // and the corner style logic is different
      cornerType = smoothCorners ? 'smooth' : 'rounded'
    }

    return {
      type: cornerType,
      radius: cornerRadius,
    }
  }
  return undefined
}

function getLayerAppearance(
  layerInInspectorData: InspectorDataLayer
): LayerAppearance | undefined {
  if (!('style' in layerInInspectorData)) {
    return undefined
  }

  const appearance = layerInInspectorData.style.appearance
  if (!appearance?.blendMode) {
    return undefined
  }

  return {
    blendMode: appearance.blendMode,
    opacity: appearance.opacity ?? 1,
  }
}

/**
 * Map the layers by "order" field instead of per layer uuid.
 * (More context: https://github.com/sketch-hq/Cloud/issues/17485)
 * Mapping by "order" also introduced issues, for example layers of type "shapeGroup"
 * have no children in the PR File, since the "shapeGroup" combines all its children.
 * However in the inspector json file, the children nodes exist and are counted when defining the "order"
 * For this reason we can not just do `const layersByOrderIndex = _mapKeys(inspectorData.layers, 'order')`
 * We need to recalculate a new order field after removing the nodes that are not in the PR file.
 */
function getLayersInInspectorDataByComputedOrderIndex(
  layers: InspectorDataArtboard['layers']
) {
  const layersList = Object.values(layers)
  const layersListOrdered = _sortBy(layersList, 'order')

  type Accumulator = {
    orderIndex: number
    layersByComputedOrderIndex: Record<number, InspectorDataLayer>
  }

  const reduceResult = layersListOrdered.reduce(
    (acc: Accumulator, layerInInspectorData) => {
      if (
        'isCombined' in layerInInspectorData &&
        layerInInspectorData.isCombined
      ) {
        // Skip that node
        return acc
      }

      acc.layersByComputedOrderIndex[acc.orderIndex] = layerInInspectorData
      acc.orderIndex = acc.orderIndex + 1

      return acc
    },
    { orderIndex: 0, layersByComputedOrderIndex: {} }
  )

  return reduceResult.layersByComputedOrderIndex
}

/**
 * Since we are trying to match the layers coming from the PR File with the layers coming
 * from the inspector json file using their position in the tree (because of lack of matching uuid).
 * We found multiple cases where we match the wrong node together because the trees don't match.
 * This is hard to debug so this function is there to throw an error on dev when we find one of those
 * case.
 */
function verifyTreeLayerMatchingThroughPosition(
  sketchElementNode: SketchElementNode,
  layersByOrderIndex: Record<number, InspectorDataLayer>
) {
  const IS_DEV_OR_TEST_ENV =
    process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'

  if (!IS_DEV_OR_TEST_ENV) {
    // Skip the check and hope for the best
    return
  }

  const nodeIndex = sketchElementNode.elementRenderingIndex
  const layerInInspectorData = layersByOrderIndex[nodeIndex]

  if (!isArtboardElement(sketchElementNode) && !layerInInspectorData) {
    throw new Error(
      `Layer "${sketchElementNode.name}" with uuid ${sketchElementNode.nodeUUID} (${nodeIndex}) not found in json data.`
    )
  }

  if (
    !isArtboardElement(sketchElementNode) &&
    layerInInspectorData.name !== sketchElementNode.name
  ) {
    throw new Error(
      `Layer "${sketchElementNode.name}" with uuid ${sketchElementNode.nodeUUID} (${nodeIndex}) does not match name "${layerInInspectorData.name}" in json data.`
    )
  }

  const childrenReversed = sketchElementNode.children.slice().reverse()

  childrenReversed.forEach(child => {
    verifyTreeLayerMatchingThroughPosition(child, layersByOrderIndex)
  })
}

function getContextForChildren(
  currentContext: BuildInspectorLayersTreeNodeContext,
  currentNode: InspectorLayersTreeNode
): BuildInspectorLayersTreeNodeContext {
  const contextForChildren = {
    ...currentContext,
    parentSymbolLayer:
      // If node is a symbol instance use this node as the parent symbol
      // Otherwise continue with whatever value was already there
      currentNode.type === 'symbolInstance'
        ? currentNode
        : currentContext.parentSymbolLayer,
  }

  // Updating the context, so we know if we're inside a group with tints, exports or styles
  if (!contextForChildren.isInsideGroupWithTints) {
    contextForChildren.isInsideGroupWithTints = isGroupLayerWithTints(
      currentNode
    )
  }

  if (!contextForChildren.isInsideGroupWithExports) {
    contextForChildren.isInsideGroupWithExports = isGroupLayerWithExports(
      currentNode
    )
  }

  if (!contextForChildren.isInsideGroupWithStyles) {
    contextForChildren.isInsideGroupWithStyles = isGroupLayerWithStyles(
      currentNode
    )
  }

  return contextForChildren
}

function isGroupLayerWithTints(layerNode: InspectorLayersTreeNode) {
  if (!isGroup(layerNode)) {
    return false
  }

  const enabledTintsInGroup =
    layerNode.style?.fills?.filter(fill => fill.isEnabled) || []

  return enabledTintsInGroup.length > 0
}

function isGroupLayerWithExports(layerNode: InspectorLayersTreeNode) {
  if (!isGroup(layerNode)) {
    return false
  }

  const exportFormats = layerNode.exportFormats

  return exportFormats.length > 0
}

function isGroupLayerWithStyles(layerNode: InspectorLayersTreeNode) {
  if (!isGroup(layerNode)) {
    return false
  }

  const style = layerNode.style
  const opacity = style?.appearance?.opacity ?? 1

  const hasFills = Boolean(Object.keys(style?.fills || {}).length)
  const hasShadows = Boolean(Object.keys(style?.shadows || {}).length)
  const hasOpacityChanged = opacity < 1
  const hasBlendModeChanged = style?.appearance?.blendMode !== 'normal'

  const isLayerStyled =
    hasFills || hasShadows || hasOpacityChanged || hasBlendModeChanged

  return isLayerStyled
}
